import React, { useState } from 'react';
import type { FileDetail, FileItem } from '../types';

interface Props {
  files: FileItem[];
  selectedFileDetail: FileDetail | null;
  onSelectFile: (fileId: string) => void;
  onRestoreVersion: (fileId: string, versionId: string) => Promise<void>;
  onArchiveToggle: (fileId: string) => Promise<void>;
  onOpenPreview: (fileId: string) => void;
}

export const VersionsArchiveView: React.FC<Props> = ({
  files,
  selectedFileDetail,
  onSelectFile,
  onRestoreVersion,
  onArchiveToggle,
  onOpenPreview,
}) => {
  const [filterArchivedOnly, setFilterArchivedOnly] = useState(false);

  const displayedFiles = filterArchivedOnly
    ? files.filter((f) => f.is_archived)
    : files;

  return (
    <div className="versions-archive-view" style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '1.5rem' }}>
      <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '14px', padding: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h4 style={{ margin: 0, color: '#f8fafc', fontSize: '0.95rem', fontWeight: 600 }}>
            Выберите документ
          </h4>
          <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={filterArchivedOnly}
              onChange={(e) => setFilterArchivedOnly(e.target.checked)}
            />
            Только архивные
          </label>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: '550px', overflowY: 'auto' }}>
          {displayedFiles.map((f) => {
            const isSelected = selectedFileDetail?.file_id === f.file_id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => onSelectFile(f.file_id)}
                style={{
                  background: isSelected ? 'rgba(56, 189, 248, 0.15)' : 'rgba(30, 41, 59, 0.4)',
                  border: `1px solid ${isSelected ? '#38bdf8' : 'rgba(148, 163, 184, 0.1)'}`,
                  borderRadius: '8px',
                  padding: '0.65rem 0.75rem',
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                <div style={{ fontWeight: 600, color: '#f8fafc', fontSize: '0.85rem', marginBottom: '0.2rem' }}>
                  {f.display_name}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.75rem', color: '#94a3b8' }}>
                  <span>{f.current_version_no}</span>
                  {f.is_archived ? (
                    <span style={{ color: '#f87171' }}>Архив</span>
                  ) : (
                    <span style={{ color: '#34d399' }}>Актуальная</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '14px', padding: '1.5rem' }}>
        {selectedFileDetail ? (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem', borderBottom: '1px solid rgba(148, 163, 184, 0.15)', paddingBottom: '1rem' }}>
              <div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#f8fafc', margin: '0 0 0.25rem 0' }}>
                  {selectedFileDetail.display_name}
                </h3>
                <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: 0 }}>
                  Цепочка версий документа · {selectedFileDetail.folder} · {selectedFileDetail.file_id}
                </p>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  className="btn-open-preview"
                  onClick={() => onOpenPreview(selectedFileDetail.file_id)}
                >
                  Предпросмотр
                </button>
                <button
                  type="button"
                  className="action-icon-btn"
                  style={{ color: selectedFileDetail.is_archived ? '#34d399' : '#f87171' }}
                  onClick={() => onArchiveToggle(selectedFileDetail.file_id)}
                >
                  {selectedFileDetail.is_archived ? 'Восстановить документ' : 'Архивировать документ'}
                </button>
              </div>
            </div>

            <h4 style={{ color: '#f8fafc', fontSize: '0.95rem', margin: '0 0 1rem 0' }}>
              История редакций ({selectedFileDetail.versions.length})
            </h4>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              {selectedFileDetail.versions.map((ver) => (
                <div
                  key={ver.id}
                  style={{
                    background: ver.is_current ? 'rgba(56, 189, 248, 0.08)' : 'rgba(30, 41, 59, 0.4)',
                    border: `1px solid ${ver.is_current ? 'rgba(56, 189, 248, 0.35)' : 'rgba(148, 163, 184, 0.15)'}`,
                    borderRadius: '10px',
                    padding: '1rem',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span className="qa-version-pill" style={{ fontSize: '0.82rem' }}>
                        {ver.version_no}
                      </span>
                      {ver.is_current ? (
                        <span className="qa-current-pill" style={{ background: 'rgba(56, 189, 248, 0.2)', color: '#38bdf8', borderColor: '#38bdf8' }}>
                          ● Актуальная
                        </span>
                      ) : (
                        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Архивная редакция</span>
                      )}
                    </div>

                    {!ver.is_current && (
                      <button
                        type="button"
                        className="action-icon-btn"
                        style={{ color: '#38bdf8', borderColor: 'rgba(56, 189, 248, 0.4)' }}
                        onClick={() => onRestoreVersion(selectedFileDetail.file_id, ver.version_id)}
                      >
                        Восстановить как актуальную
                      </button>
                    )}
                  </div>

                  <p style={{ margin: '0 0 0.5rem 0', color: '#cbd5e1', fontSize: '0.88rem' }}>
                    {ver.change_note || 'Описание изменений отсутствует'}
                  </p>

                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.78rem', color: '#94a3b8' }}>
                    <span>Дата вступления: {ver.effective_date || 'Не указана'}</span>
                    <span>Источник: {ver.source}</span>
                    <span>Автор редакции: {ver.created_by}</span>
                    <span>Хэш: {ver.checksum.slice(0, 12)}...</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '4rem 1rem', color: '#94a3b8' }}>
            Выберите документ из списка слева для просмотра цепочки версий
          </div>
        )}
      </div>
    </div>
  );
};
